import React from "react";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { formatAuthError, useAuth } from "@core/auth/AuthProvider";
import { VenProLogo } from "@core/components/VenProLogo";

function GoogleMark() {
  return (
    <svg className="google-mark" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.55h3.24c1.9-1.75 2.98-4.33 2.98-7.42Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.98-.9 6.63-2.35l-3.24-2.55c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.63A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.93A6.02 6.02 0 0 1 6.07 12c0-.67.11-1.32.32-1.93V7.44H3.04A10 10 0 0 0 2 12c0 1.64.39 3.2 1.04 4.56l3.35-2.63Z" />
      <path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.65 9.65 0 0 0 12 2a10 10 0 0 0-8.96 5.44l3.35 2.63C7.18 7.7 9.39 5.94 12 5.94Z" />
    </svg>
  );
}

export function LoginPage({ initialMode }) {
  const {
    cancelMfaSignIn,
    completeMfaSignIn,
    isConfigured,
    isAppCheckConfigured,
    isDemoMode,
    mfaRequired,
    signInWithGoogle,
    signInWithPassword,
    signUpWithGoogle,
    signUpWithPassword,
    sendPasswordReset,
  } = useAuth();

  const [mode, setMode] = React.useState(() => {
    if (initialMode) return initialMode;
    const searchMode = new URLSearchParams(window.location.search).get("mode");
    if (searchMode === "signup" || window.location.pathname === "/signup") return "signup";
    return "login";
  });
  const [identifier, setIdentifier] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState("");
  const [infoMessage, setInfoMessage] = React.useState("");
  const [mfaCode, setMfaCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const clearMessages = () => {
    setError("");
    setInfoMessage("");
  };

  const switchMode = (nextMode) => {
    clearMessages();
    setMode(nextMode);
    if (window.history?.replaceState) {
      const targetPath = nextMode === "signup" ? "/signup" : "/login";
      window.history.replaceState(null, "", targetPath);
    }
  };

  const handleGoogleSignIn = async () => {
    setBusy(true);
    clearMessages();
    try {
      if (mode === "signup") {
        await signUpWithGoogle();
      } else {
        await signInWithGoogle();
      }
    } catch (nextError) {
      setError(formatAuthError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSignIn = async (event) => {
    event.preventDefault();
    setBusy(true);
    clearMessages();
    try {
      await signInWithPassword(identifier, password);
    } catch (nextError) {
      setError(formatAuthError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    clearMessages();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    try {
      await signUpWithPassword({
        name: form.get("name"),
        email: form.get("email"),
        password: form.get("password"),
        businessName: form.get("businessName"),
        businessType: form.get("businessType"),
      });
    } catch (nextError) {
      setError(formatAuthError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    clearMessages();
    setBusy(true);
    try {
      await sendPasswordReset(identifier);
      setInfoMessage(`Password reset link sent to ${identifier}. Check your inbox.`);
    } catch (nextError) {
      setError(formatAuthError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const handleMfa = async (event) => {
    event.preventDefault();
    setBusy(true);
    clearMessages();
    try {
      await completeMfaSignIn(mfaCode);
      setMfaCode("");
    } catch (nextError) {
      setError(formatAuthError(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page login-split">
      <section className="login-visual" aria-label="VenPro for independent service businesses">
        <img src="/media/vendor-workshop-hero.png" alt="Technician working in shop" />
        <div className="login-visual-overlay" aria-hidden="true" />
        <header className="login-visual-header">
          <VenProLogo className="login-hero-logo" variant="horizontal" />
          <span>Vendor operations</span>
        </header>
        <div className="login-visual-content">
          <p className="eyebrow">VENDOR OPERATIONS</p>
          <h2>Dispatch jobs.<br />Prove the work.<br />Get paid.</h2>
          <p className="login-visual-copy">Assign technicians, track mobile routes, attach completion photos, and bill dealership accounts.</p>
          <ul>
            <li><CheckCircle2 /> Real-time job board and mobile dispatch</li>
            <li><CheckCircle2 /> Before and after photo verification</li>
            <li><CheckCircle2 /> Itemized dealership invoices and aging balances</li>
          </ul>
        </div>
        <footer className="login-visual-footer">
          <span><span className="login-live-dot" /> Dedicated vendor system</span>
          <span>Independent service operations</span>
        </footer>
      </section>
      <section className="login-auth" aria-labelledby="login-title">
        <div className="login-card">
          <div className="login-card-brand">
            <VenProLogo className="login-logo" />
            <div>
              <strong>VenPro</strong>
              <span>Vendor operations</span>
            </div>
          </div>

          <div className="login-heading">
            <h1 id="login-title">
              {mode === "login"
                ? "Sign in to your shop"
                : mode === "signup"
                  ? "Create shop workspace"
                  : "Reset password"}
            </h1>
            <p className="muted">
              {mode === "login"
                ? "Sign in with your email and password, or use Google."
                : mode === "signup"
                  ? "Enter your details to start dispatching jobs and billing dealerships."
                  : "Enter your work email to receive a password reset link."}
            </p>
          </div>

          {error && <div className="notice notice-danger" role="alert">{error}</div>}
          {infoMessage && <div className="notice notice-success" role="status">{infoMessage}</div>}

          {mfaRequired ? (
            <form className="signup-form" onSubmit={handleMfa}>
              <div className="notice notice-warning">Enter the 6-digit code from your authenticator app.</div>
              <label>
                <span>Authenticator code</span>
                <input
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  placeholder="000000"
                  autoFocus
                  required
                />
              </label>
              <button className="button button-primary button-wide" type="submit" disabled={busy || mfaCode.length !== 6}><ShieldCheck /> {busy ? "Verifying…" : "Verify code"}</button>
              <button className="button button-secondary button-wide" type="button" disabled={busy} onClick={() => { cancelMfaSignIn(); setMfaCode(""); }}>Cancel</button>
            </form>
          ) : (!isConfigured || !isAppCheckConfigured) && !isDemoMode ? (
            <div className="notice notice-warning" role="alert">
              Configuration required before sign-in can be enabled.
            </div>
          ) : mode === "login" ? (
            <>
              <form className="signup-form" onSubmit={handlePasswordSignIn}>
                <label>
                  <span>Email or username</span>
                  <input
                    type="text"
                    name="identifier"
                    autoComplete="username"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="name@company.com"
                    required
                  />
                </label>
                <label>
                  <div className="login-label-row">
                    <span>Password</span>
                    <button
                      type="button"
                      className="login-forgot-button"
                      onClick={() => switchMode("reset")}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="login-password-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      required
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      title={showPassword ? "Hide password" : "Show password"}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </label>
                <button className="button button-primary button-wide" type="submit" disabled={busy}>
                  <LockKeyhole size={15} /> {busy ? "Signing in…" : "Sign in"} <ArrowRight size={15} />
                </button>
              </form>

              <div className="login-divider">
                <span>OR</span>
              </div>

              <button className="login-google-button" type="button" disabled={busy} onClick={handleGoogleSignIn}>
                {isDemoMode ? <LockKeyhole aria-hidden="true" /> : <GoogleMark />}
                <span>{busy ? "Signing in…" : isDemoMode ? "Enter demo mode" : "Continue with Google"}</span>
                <ArrowRight aria-hidden="true" />
              </button>
            </>
          ) : mode === "signup" ? (
            <>
              <form className="signup-form" onSubmit={handleSignup}>
                <div className="signup-form-row">
                  <label>
                    <span>Full name</span>
                    <input name="name" autoComplete="name" placeholder="Full name" required />
                  </label>
                  <label>
                    <span>Work email</span>
                    <input name="email" type="email" autoComplete="email" placeholder="name@company.com" required />
                  </label>
                </div>
                <div className="signup-form-row">
                  <label>
                    <span>Business name</span>
                    <input name="businessName" autoComplete="organization" placeholder="Business name" required />
                  </label>
                  <label>
                    <span>Primary service</span>
                    <select name="businessType" defaultValue="Window tint & protection">
                      <option>Window tint & protection</option>
                      <option>Interior repair</option>
                      <option>Detailing</option>
                      <option>Mobile service</option>
                      <option>Paintless dent repair</option>
                      <option>Other service</option>
                    </select>
                  </label>
                </div>
                <label>
                  <span>Password</span>
                  <div className="login-password-wrapper">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      minLength={6}
                      required
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      title={showPassword ? "Hide password" : "Show password"}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </label>
                <button className="button button-primary button-wide" type="submit" disabled={busy}>
                  <Building2 size={15} /> {busy ? "Creating workspace…" : "Create workspace"} <ArrowRight size={15} />
                </button>
              </form>

              <div className="login-divider">
                <span>OR</span>
              </div>

              <button className="login-google-button" type="button" disabled={busy} onClick={handleGoogleSignIn}>
                <GoogleMark />
                <span>{busy ? "Signing up…" : "Sign up with Google"}</span>
                <ArrowRight aria-hidden="true" />
              </button>
            </>
          ) : (
            <form className="signup-form" onSubmit={handleResetPassword}>
              <label>
                <span>Work email</span>
                <input
                  type="email"
                  name="resetEmail"
                  autoComplete="email"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="name@company.com"
                  required
                />
              </label>
              <button className="button button-primary button-wide" type="submit" disabled={busy}>
                <Mail size={15} /> {busy ? "Sending…" : "Send reset link"} <ArrowRight size={15} />
              </button>
              <button
                className="button button-secondary button-wide"
                type="button"
                disabled={busy}
                onClick={() => switchMode("login")}
              >
                Back to sign in
              </button>
            </form>
          )}

          <div className="login-mode-row">
            {mode === "login" ? (
              <>
                <span>Need a workspace?</span>
                <button
                  className="login-mode-switch"
                  type="button"
                  onClick={() => switchMode("signup")}
                >
                  Create one
                </button>
              </>
            ) : mode === "signup" ? (
              <>
                <span>Have an account?</span>
                <button
                  className="login-mode-switch"
                  type="button"
                  onClick={() => switchMode("login")}
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                <span>Remembered password?</span>
                <button
                  className="login-mode-switch"
                  type="button"
                  onClick={() => switchMode("login")}
                >
                  Sign in
                </button>
              </>
            )}
          </div>

          <div className="login-security">
            <ShieldCheck size={14} aria-hidden="true" />
            <span>Private tenant storage. Isolated to your business.</span>
          </div>
        </div>
        <footer className="login-auth-footer"><span>© 2026 VenPro</span><span>Tenant-scoped storage</span></footer>
      </section>
    </main>
  );
}
